/**
 * Amplitude Session Replay Integration with Segment
 *
 * This module integrates Amplitude Session Replay with the existing Segment implementation.
 * It follows the Amplitude (Actions) destination pattern.
 *
 * @see https://amplitude.com/docs/session-replay/session-replay-integration-with-segment#amplitude-actions-destination
 */

import * as sessionReplay from '@amplitude/session-replay-browser'

// Cookie utility functions
const cookie = {
  get: (name) => {
    const value = `; ${document.cookie}`
    const parts = value.split(`; ${name}=`)
    if (parts.length === 2) return parts.pop().split(';').shift()
    return null
  },
  set: (name, value) => {
    document.cookie = `${name}=${value}; path=/; max-age=31536000; SameSite=Lax`
  },
  remove: (name) => {
    document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax`
  },
}

// Get the stored session ID from Amplitude's cookie
const getStoredSessionId = () => {
  return cookie.get('analytics_session_id') || 0
}

// Generate a UUID v4
const generateUUID = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.floor(Math.random() * 16)
    const v = c === 'x' ? r : (r % 4) + 8
    return v.toString(16)
  })
}

/**
 * Check if Session Replay should be blocked on current page
 * Returns true if page contains sensitive student data
 */
const shouldBlockSessionReplay = (debugMode = false) => {
  const currentPath = window.location.pathname

  // Block Session Replay on pages that display student PII
  const blockedPaths = [
    '/student/', // Any student-facing pages
    // Add more paths as needed
  ]

  const isBlocked = blockedPaths.some((path) => currentPath.includes(path))

  if (debugMode) {
    console.log('[Session Replay] 🔍 Path check:', {
      currentPath,
      isBlocked,
      blockedPaths,
    })
  }

  return isBlocked
}

/**
 * Monitor route changes and stop/start Session Replay based on current page
 */
const setupRouteMonitoring = (
  amplitudeApiKey,
  segmentAnalytics,
  amplitudeSampleRate,
  debugMode = false
) => {
  let currentPath = window.location.pathname
  let isCurrentlyBlocked = shouldBlockSessionReplay(debugMode)
  let isSessionReplayActive = !isCurrentlyBlocked

  // Store the interval ID so we can clear it later
  const intervalId = setInterval(async () => {
    const newPath = window.location.pathname

    if (newPath !== currentPath) {
      currentPath = newPath
      const shouldBlock = shouldBlockSessionReplay(debugMode)

      // Route changed to a blocked page
      if (shouldBlock && !isCurrentlyBlocked) {
        if (debugMode) {
          console.log(
            '[Session Replay] ⛔ Navigated to blocked page - stopping recording'
          )
        }
        isCurrentlyBlocked = true
        isSessionReplayActive = false

        // Stop the session replay recording
        if (window.sessionReplay) {
          try {
            await sessionReplay.shutdown()
            delete window.sessionReplay
            if (debugMode) {
              console.log('[Session Replay] 🛑 Recording stopped')
            }
          } catch (error) {
            console.error('[Session Replay] Error stopping recording:', error)
          }
        }
      }
      // Route changed from blocked page to allowed page - RESTART Session Replay
      else if (!shouldBlock && isCurrentlyBlocked && !isSessionReplayActive) {
        if (debugMode) {
          console.log(
            '[Session Replay] ✅ Navigated to allowed page - restarting recording'
          )
        }
        isCurrentlyBlocked = false

        // Restart Session Replay
        try {
          // Get device ID from Segment or localStorage
          let deviceId = null
          try {
            deviceId =
              segmentAnalytics?._user?.anonymousId ||
              JSON.parse(localStorage.getItem('ajs_anonymous_id')) ||
              generateUUID()
          } catch (error) {
            deviceId = generateUUID()
          }

          // Get or create session ID
          let sessionId = getStoredSessionId()
          if (!sessionId || sessionId === 0) {
            sessionId = Date.now()
            cookie.set('analytics_session_id', sessionId)
          }

          // Initialize config with Amplitude's built-in masking
          const initConfig = {
            deviceId,
            sessionId,
            sampleRate: amplitudeSampleRate || 1.0,
            maskTextClass: 'amp-mask',
            maskAllInputs: true,
            blockClass: 'amp-block',
          }

          if (debugMode) {
            console.log(
              '[Session Replay] 🔄 Reinitializing with config:',
              initConfig
            )
          }

          // Initialize Session Replay
          await sessionReplay.init(amplitudeApiKey, initConfig).promise

          // Expose globally for debugging
          window.sessionReplay = sessionReplay
          isSessionReplayActive = true

          if (debugMode) {
            console.log('[Session Replay] ✅ Recording restarted successfully')
          }
        } catch (error) {
          console.error('[Session Replay] ❌ Failed to restart:', error)
        }
      }
    }
  }, 500) // Check every 500ms for route changes

  // Store the interval ID for cleanup
  if (typeof window !== 'undefined') {
    window._sessionReplayRouteMonitor = intervalId
  }
}

/**
 * Initialize Amplitude Session Replay
 *
 * @param {string} amplitudeApiKey - Amplitude API Key (must match Segment's Amplitude destination)
 * @param {object} segmentAnalytics - The Segment analytics instance
 * @param {number} amplitudeSampleRate - Sample rate for session replay (0.0 to 1.0)
 * @param {boolean} debugMode - Enable debug mode (exposes window.sessionReplay for testing)
 * @returns {Promise<void>}
 */
export const initializeSessionReplay = async (
  amplitudeApiKey,
  segmentAnalytics,
  amplitudeSampleRate = 1.0,
  debugMode = false
) => {
  // Check if we should block Session Replay on this page
  if (shouldBlockSessionReplay(debugMode)) {
    if (debugMode) {
      console.log(
        '[Session Replay] ⛔ Blocked on this page (contains student PII)'
      )
    }
    // Still setup route monitoring even if blocked initially
    // So it can restart when navigating to allowed pages
    setupRouteMonitoring(
      amplitudeApiKey,
      segmentAnalytics,
      amplitudeSampleRate,
      debugMode
    )
    return
  }
  if (!amplitudeApiKey) {
    console.warn(
      '[Session Replay] ⚠️ Amplitude API Key not provided. Session Replay will not be initialized.'
    )
    return
  }

  if (!segmentAnalytics) {
    console.error(
      '[Session Replay] ❌ Segment analytics instance not provided.'
    )
    return
  }

  try {
    const storedSessionId = getStoredSessionId()

    // Get device ID from Segment's internal state
    // The custom Segment implementation stores this in the analytics object
    let deviceId

    // Try to get anonymousId from Segment (it may be set after the script loads)
    if (typeof segmentAnalytics.user === 'function') {
      // Standard Segment Analytics.js
      const user = await segmentAnalytics.user()
      deviceId = user.anonymousId()
    } else if (segmentAnalytics._user && segmentAnalytics._user.anonymousId) {
      // Alternative internal reference
      deviceId = segmentAnalytics._user.anonymousId()
    } else {
      // Fallback: generate or retrieve device ID from localStorage
      const storedDeviceId = localStorage.getItem('ajs_anonymous_id')
      if (storedDeviceId) {
        // Remove quotes if present
        deviceId = storedDeviceId.replace(/^"|"$/g, '')
      } else {
        // Generate a new device ID if none exists
        deviceId = generateUUID()
        localStorage.setItem('ajs_anonymous_id', `"${deviceId}"`)
      }
    }

    if (debugMode) {
      console.log('[Session Replay] Using device ID:', deviceId)
      console.log('[Session Replay] Using session ID:', storedSessionId)
    }

    // Initialize Session Replay with the current session ID and device ID
    const initConfig = {
      deviceId,
    }

    // Generate a session ID if none exists
    // Amplitude session IDs are typically Unix timestamps in milliseconds
    let sessionId = storedSessionId

    if (!sessionId || sessionId === 0) {
      // Generate a new session ID (Unix timestamp)
      sessionId = Date.now()
      cookie.set('analytics_session_id', sessionId)
      if (debugMode) {
        console.log('[Session Replay] 📝 Generated new session ID:', sessionId)
      }
    } else if (debugMode) {
      console.log('[Session Replay] 📝 Using existing session ID:', sessionId)
    }

    initConfig.sessionId = sessionId

    // Add sample rate if provided
    if (amplitudeSampleRate && amplitudeSampleRate !== '1.0') {
      initConfig.sampleRate = parseFloat(amplitudeSampleRate)
    }

    // Privacy: Mask elements containing student PII
    // Amplitude's built-in masking: Use .amp-mask class
    // Elements with this class will have their text displayed as *** in recordings
    initConfig.maskTextClass = 'amp-mask'

    // Mask all input fields for additional privacy
    initConfig.maskAllInputs = true

    // Block elements with .amp-block class (completely hidden from recording)
    initConfig.blockClass = 'amp-block'

    if (debugMode) {
      console.log('[Session Replay] Initializing with config:', initConfig)
    }

    const result = await sessionReplay.init(amplitudeApiKey, initConfig).promise

    if (debugMode) {
      console.log('[Session Replay] Init result:', result)

      // Verify initialization
      const props = sessionReplay.getSessionReplayProperties()
      console.log('[Session Replay] Properties after init:', props)

      if (!props || Object.keys(props).length === 0) {
        console.warn('[Session Replay] ⚠️ Initialized but properties are empty')
        console.warn(
          '[Session Replay]    This might be normal - properties may be set after first event'
        )
      }
    }

    // Expose sessionReplay globally for testing and debugging (only in debug mode)
    if (debugMode) {
      window.sessionReplay = sessionReplay
      window.amplitudeSessionReplayDebug = {
        getProperties: () => sessionReplay.getSessionReplayProperties(),
        getDeviceId: () => localStorage.getItem('ajs_anonymous_id'),
        getSessionId: () => cookie.get('analytics_session_id'),
        setSessionId: (id) => sessionReplay.setSessionId(id),
        config: {
          apiKey: amplitudeApiKey ? '✅ Set' : '❌ Missing',
          sampleRate: amplitudeSampleRate,
          deviceId: initConfig.deviceId,
          sessionId: initConfig.sessionId,
        },
        test: async () => {
          console.group('🧪 Session Replay Test')
          console.log('✅ Session Replay initialized')

          const propsBeforeEvent = sessionReplay.getSessionReplayProperties()
          console.log('Properties (before event):', propsBeforeEvent)
          console.log('Device ID:', localStorage.getItem('ajs_anonymous_id'))
          console.log(
            'Session ID (cookie):',
            cookie.get('analytics_session_id')
          )
          console.log('Session ID (stored):', getStoredSessionId())
          console.log('Config:', {
            apiKey: amplitudeApiKey ? '✅ Set' : '❌ Missing',
            sampleRate: amplitudeSampleRate,
            deviceId: initConfig.deviceId,
            sessionId: initConfig.sessionId,
          })

          // Try tracking a test event and check if properties appear
          if (window.analytics && window.analytics.track) {
            console.log('\n📤 Tracking test event to trigger session...')
            window.analytics.track('Session Replay Test Event', {
              timestamp: new Date().toISOString(),
              test: true,
            })

            // Wait a bit for the event to process
            await new Promise((resolve) => setTimeout(resolve, 1000))

            const propsAfterEvent = sessionReplay.getSessionReplayProperties()
            console.log('Properties (after event):', propsAfterEvent)

            if (propsAfterEvent && Object.keys(propsAfterEvent).length > 0) {
              console.log('✅ Session Replay properties populated after event!')
            } else {
              console.warn('⚠️ Properties still empty after event')
            }
          }

          console.groupEnd()
        },
      }
      console.log(
        '[Session Replay] 🐛 Debug mode enabled - window.sessionReplay and window.amplitudeSessionReplayDebug are available'
      )
      console.log(
        '[Session Replay]    Run window.amplitudeSessionReplayDebug.test() to run diagnostics'
      )
    } else {
      // In production, still expose sessionReplay for proper functionality
      window.sessionReplay = sessionReplay
    }

    if (debugMode) {
      console.log(
        '[Session Replay] ✅ Amplitude Session Replay initialized successfully'
      )
    }

    // Add middleware to check if the session ID has changed
    // and update the session replay instance
    segmentAnalytics.addSourceMiddleware(({ payload, next }) => {
      try {
        const currentStoredSessionId = getStoredSessionId()

        // Check for Amplitude Actions session ID in different possible locations
        const nextSessionId =
          payload.obj.integrations?.['Actions Amplitude']?.session_id ||
          payload.obj.integrations?.['Amplitude (Actions)']?.session_id ||
          payload.obj.integrations?.Amplitude?.session_id ||
          0

        if (debugMode) {
          console.log('[Session Replay] 🔍 Middleware - Session ID check:', {
            stored: currentStoredSessionId,
            next: nextSessionId,
            integrations: payload.obj.integrations,
          })
        }

        if (
          nextSessionId &&
          nextSessionId > 0 &&
          currentStoredSessionId < nextSessionId
        ) {
          cookie.set('analytics_session_id', nextSessionId)
          sessionReplay.setSessionId(nextSessionId)

          if (debugMode) {
            console.log(
              '[Session Replay] 🔄 Session ID updated:',
              nextSessionId
            )
            const updatedProps = sessionReplay.getSessionReplayProperties()
            console.log('[Session Replay]    Updated properties:', updatedProps)
          }
        }
      } catch (error) {
        console.error('[Session Replay] Error updating session ID:', error)
      }
      next(payload)
    })

    // Wrap track and page methods directly using Object.defineProperty
    // Note: We can't use addSourceMiddleware because Segment is already initialized by the time we run
    if (debugMode) {
      console.log(
        '[Session Replay] 🔧 Wrapping Segment methods to inject Session Replay ID...'
      )
    }

    // CRITICAL: Wrap window.analytics, not just the local reference
    const analytics = window.analytics
    const originalTrack = analytics.track.bind(analytics)
    const originalPage = analytics.page.bind(analytics)
    const originalIdentify = analytics.identify.bind(analytics)

    // Wrap methods - use writable: true so debugger can wrap them too
    window.analytics.track = function wrappedTrack(
      event,
      properties,
      options,
      callback
    ) {
      const sessionReplayProperties = sessionReplay.getSessionReplayProperties()
      const enrichedProperties = {
        ...properties,
        ...sessionReplayProperties,
      }

      if (debugMode) {
        console.log(
          '[Session Replay] ✅ Track:',
          event,
          sessionReplayProperties['[Amplitude] Session Replay ID']
        )
      }

      return originalTrack(event, enrichedProperties, options, callback)
    }

    window.analytics.page = function wrappedPage(
      category,
      name,
      properties,
      options,
      callback
    ) {
      // Handle overloaded page() method
      let finalCategory
      let finalName
      let finalProperties
      let finalOptions
      let finalCallback

      if (typeof category === 'object') {
        finalProperties = category
        finalOptions = name
        finalCallback = properties
      } else if (typeof name === 'object') {
        finalCategory = category
        finalProperties = name
        finalOptions = properties
        finalCallback = options
      } else {
        finalCategory = category
        finalName = name
        finalProperties = properties
        finalOptions = options
        finalCallback = callback
      }

      const sessionReplayProperties = sessionReplay.getSessionReplayProperties()
      const enrichedProperties = {
        ...finalProperties,
        ...sessionReplayProperties,
      }

      if (debugMode) {
        console.log(
          '[Session Replay] ✅ Page:',
          sessionReplayProperties['[Amplitude] Session Replay ID']
        )
      }

      return originalPage(
        finalCategory,
        finalName,
        enrichedProperties,
        finalOptions,
        finalCallback
      )
    }

    window.analytics.identify = function wrappedIdentify(
      userId,
      traits,
      options,
      callback
    ) {
      const sessionReplayProperties = sessionReplay.getSessionReplayProperties()
      const enrichedTraits = {
        ...traits,
        ...sessionReplayProperties,
      }

      if (debugMode) {
        console.log(
          '[Session Replay] ✅ Identify:',
          userId,
          sessionReplayProperties['[Amplitude] Session Replay ID']
        )
      }

      return originalIdentify(userId, enrichedTraits, options, callback)
    }

    if (debugMode) {
      console.log(
        '[Session Replay] ✅ Methods wrapped successfully (track, page, identify)'
      )
      console.log(
        '[Session Replay] 🔍 Verify: window.analytics.track === wrapped?',
        window.analytics.track.toString().includes('Session Replay')
      )
      console.log(
        '[Session Replay] ✅ Segment middleware for Session Replay added successfully'
      )
    }

    // Setup route monitoring to stop/start recording based on navigation
    setupRouteMonitoring(
      amplitudeApiKey,
      segmentAnalytics,
      amplitudeSampleRate,
      debugMode
    )

    if (debugMode) {
      console.log('[Session Replay] 🔍 Route monitoring active')
    }
  } catch (error) {
    console.error(
      '[Session Replay] ❌ Failed to initialize Amplitude Session Replay:',
      error
    )
    console.error('[Session Replay] Error details:', error.message)
    console.error('[Session Replay] Stack trace:', error.stack)
    throw error // Re-throw to be caught by caller
  }
}

/**
 * Get current session replay properties
 * Useful for debugging or manual tracking
 *
 * @returns {object} Session replay properties
 */
export const getSessionReplayProperties = () => {
  try {
    return sessionReplay.getSessionReplayProperties()
  } catch (error) {
    console.error('Error getting session replay properties:', error)
    return {}
  }
}

/**
 * Manually set session ID
 * Use this if you need to sync session IDs manually
 *
 * @param {number} sessionId - The new session ID
 */
export const setSessionId = (sessionId) => {
  try {
    sessionReplay.setSessionId(sessionId)
    cookie.set('analytics_session_id', sessionId)
  } catch (error) {
    console.error('[Session Replay] Error setting session ID:', error)
  }
}

/**
 * Shutdown session replay
 * Call this when cleaning up or logging out
 */
export const shutdownSessionReplay = async () => {
  try {
    // Clear route monitoring interval
    if (typeof window !== 'undefined' && window._sessionReplayRouteMonitor) {
      clearInterval(window._sessionReplayRouteMonitor)
      delete window._sessionReplayRouteMonitor
    }

    await sessionReplay.shutdown()
  } catch (error) {
    console.error('[Session Replay] Error shutting down:', error)
  }
}

/**
 * Reset Session Replay on logout
 * This should be called when a user logs out to ensure the next user gets a fresh session
 * Clears session ID cookie and Segment's anonymousId
 *
 * @returns {void}
 */
export const resetSessionReplay = () => {
  try {
    // Clear the session ID cookie
    cookie.remove('analytics_session_id')

    // Clear Segment's anonymous ID from localStorage (will be regenerated on next login)
    localStorage.removeItem('ajs_anonymous_id')
  } catch (error) {
    console.error('[Session Replay] ❌ Error resetting Session Replay:', error)
  }
}

/**
 * Check and reset Session Replay if needed on login
 * This is a safety mechanism for cases where logout wasn't properly called
 * (e.g., session timeout, force logout, browser crash)
 *
 * @param {string} currentUserId - The ID of the user currently logging in
 * @returns {void}
 */
export const checkAndResetSessionReplayOnLogin = (currentUserId) => {
  try {
    // Check if there's a stored user ID in localStorage from previous session
    const lastUserId = localStorage.getItem('last_session_replay_user_id')

    // If the current user is different from the last user, reset Session Replay
    if (lastUserId && lastUserId !== currentUserId) {
      resetSessionReplay()
    }

    // Store the current user ID for next login check
    localStorage.setItem('last_session_replay_user_id', currentUserId)
  } catch (error) {
    console.error(
      '[Session Replay] ❌ Error checking Session Replay on login:',
      error
    )
  }
}

export default {
  initializeSessionReplay,
  getSessionReplayProperties,
  setSessionId,
  shutdownSessionReplay,
  resetSessionReplay,
  checkAndResetSessionReplayOnLogin,
}
